import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  CardMedia, 
  Tabs, 
  Tab, 
  Divider,
  CircularProgress,
  useTheme,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditProfile from './EditProfile';
import { styled } from '@mui/system';
import { supabase } from '../supabaseClient';
import { AchievementGallery } from './AchievementBadge';
import SampleProjectsGallery from './SampleProjectsGallery';
import { useNavigate, Navigate } from 'react-router-dom';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.2)' 
    : '0 8px 32px rgba(0, 0, 0, 0.06)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(26, 26, 37, 0.95)' 
    : '#FFFFFF',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
  padding: theme.spacing(3),
  height: '100%'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '10px 24px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 12px rgba(138, 43, 226, 0.3)' 
    : '0 4px 12px rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 6px 16px rgba(138, 43, 226, 0.4)' 
      : '0 6px 16px rgba(138, 43, 226, 0.3)',
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '1rem',
    minWidth: 120,
    borderRadius: 12,
    padding: '12px 24px',
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(138, 43, 226, 0.1)' 
        : 'rgba(138, 43, 226, 0.05)',
    },
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.05)' 
        : 'rgba(0, 0, 0, 0.05)',
      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: 1.5,
  }
}));

const StatCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(26, 26, 37, 0.8)' 
    : 'rgba(255, 255, 255, 0.8)',
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 20px rgba(0, 0, 0, 0.2)' 
    : '0 4px 20px rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 30px rgba(0, 0, 0, 0.3)' 
      : '0 8px 30px rgba(0, 0, 0, 0.1)',
  }
}));

const UserDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [userStats, setUserStats] = useState({
    totalImages: 0,
    totalProperties: 0,
    creditsRemaining: 0,
    achievementPoints: 0
  });
  const [recentImages, setRecentImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/login', {
            state: {
              returnUrl: '/dashboard',
              message: 'Gösterge panelinizi görüntülemek için lütfen giriş yapın'
            }
          });
          return;
        }
        
        if (user) {
          // Fetch user profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
            
          setUserProfile(profile);
          
          // Fetch user stats
          const { data: images, count: imagesCount } = await supabase
            .from('images')
            .select('*', { count: 'exact' })
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(4);
            
          const { count: propertiesCount } = await supabase
            .from('properties')
            .select('*', { count: 'exact' })
            .eq('user_id', user.id);
            
          setUserStats({
            totalImages: imagesCount || 0,
            totalProperties: propertiesCount || 0,
            creditsRemaining: profile?.credits_remaining || 0,
            achievementPoints: profile?.achievement_points || 0
          });
          
          setRecentImages(images || []);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleSelectSampleProject = (project) => {
    // Navigate to canvas editor with the selected sample project
    navigate(`/canvas-editor?workflow=${project.workflow || 'interior-redesign'}`, {
      state: {
        sampleProject: project
      }
    });
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '50vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ py: 4, maxWidth: '1400px', mx: 'auto', px: { xs: 2, sm: 3, md: 4 } }}>
      <EditProfile 
        open={editProfileOpen} 
        onClose={() => setEditProfileOpen(false)} 
        user={userProfile} 
        onUpdate={(session, profile) => {
          // Update local state if needed
          if (profile) {
            setUserProfile(prev => ({...prev, ...profile}));
          }
        }} 
      />
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 1, 
              fontWeight: 700,
              position: 'relative',
              display: 'inline-block',
              cursor: 'default',
              '&:hover::after': {
                content: '"🏆"',
                position: 'absolute',
                right: -30,
                animation: 'bounce 0.5s infinite alternate',
                '@keyframes bounce': {
                  from: { transform: 'translateY(0)' },
                  to: { transform: 'translateY(-10px)' }
                }
              }
            }}
            onClick={(e) => {
              // Easter egg: Click on welcome message
              const clickCount = parseInt(localStorage.getItem('welcomeClickCount') || '0');
              localStorage.setItem('welcomeClickCount', (clickCount + 1).toString());
              
              if (clickCount >= 4) {
                // After 5 clicks, show funny message
                const funnyGreetings = [
                  `Hoş Geldiniz, Süper Mimar ${userProfile?.name || 'Kullanıcı'} 🦸`,
                  `Merhaba Mimarlık Dehası ${userProfile?.name || 'Kullanıcı'} 🧠`,
                  `Selam, Tasarım Ustası ${userProfile?.name || 'Kullanıcı'} 🎨`,
                  `Vay! Efsanevi ${userProfile?.name || 'Kullanıcı'} Burada! 🌟`,
                  `Mimarlık Dünyasının Yıldızı ${userProfile?.name || 'Kullanıcı'} 🌠`
                ];
                
                e.target.innerText = funnyGreetings[Math.floor(Math.random() * funnyGreetings.length)];
                
                // Add confetti effect
                const confetti = document.createElement('div');
                confetti.style.position = 'fixed';
                confetti.style.top = '0';
                confetti.style.left = '0';
                confetti.style.width = '100%';
                confetti.style.height = '100%';
                confetti.style.pointerEvents = 'none';
                confetti.style.zIndex = '9999';
                document.body.appendChild(confetti);
                
                // Create confetti particles
                for (let i = 0; i < 100; i++) {
                  const particle = document.createElement('div');
                  particle.style.position = 'absolute';
                  particle.style.width = '10px';
                  particle.style.height = '10px';
                  particle.style.backgroundColor = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'][Math.floor(Math.random() * 6)];
                  particle.style.borderRadius = '50%';
                  particle.style.top = '0';
                  particle.style.left = `${Math.random() * 100}%`;
                  particle.style.transform = 'translateY(-10px)';
                  particle.style.animation = `fall ${1 + Math.random() * 3}s linear forwards`;
                  confetti.appendChild(particle);
                }
                
                // Add animation style
                const style = document.createElement('style');
                style.innerHTML = `
                  @keyframes fall {
                    to {
                      transform: translateY(100vh) rotate(${Math.random() * 360}deg);
                    }
                  }
                `;
                document.head.appendChild(style);
                
                // Remove confetti after animation
                setTimeout(() => {
                  document.body.removeChild(confetti);
                  document.head.removeChild(style);
                  // Reset click count
                  localStorage.setItem('welcomeClickCount', '0');
                  // Reset text
                  e.target.innerText = `Hoş Geldiniz, ${userProfile?.display_name || userProfile?.full_name || userProfile?.name || 'Kullanıcı'}`;
                }, 3000);
              }
            }}
          >
            Hoş Geldiniz, {userProfile?.display_name || userProfile?.full_name || userProfile?.name || 'Kullanıcı'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sanalyapı'daki kişisel gösterge panelinizdeki istatistiklerinizi ve başarılarınızı görüntüleyin.
          </Typography>
        </Box>
        <Tooltip title="Profili Düzenle">
          <IconButton 
            onClick={() => setEditProfileOpen(true)}
            sx={{
              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              borderRadius: '12px',
              padding: '10px',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 1, 
                fontWeight: 700,
                color: theme.palette.primary.main,
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                // Easter egg: When clicking on stats
                if (userStats.totalImages === 0) {
                  // Funny message for users with no images
                  const messages = [
                    'Henüz görsel yok! Başlamaya ne dersiniz? 🚀',
                    'Boş sayfa sendromu mu? İlk görselinizi oluşturun! 🖼️',
                    'Burada çöl gibi boş! Biraz renk katalım! 🎨',
                    'İlk adımı atmak en zoru! Hadi başlayalım! 🏃'
                  ];
                  alert(messages[Math.floor(Math.random() * messages.length)]);
                } else if (userStats.totalImages > 50) {
                  // Funny message for power users
                  const messages = [
                    'Vay canına! Gerçek bir görsel ustasısınız! 🏆',
                    'Bu kadar çok görsel! Gözleriniz yorulmadı mı? 👀',
                    'Koleksiyonunuz müzelik olmuş! 🖼️',
                    'Mimarlık dünyasının Picasso\'su! 🎨'
                  ];
                  alert(messages[Math.floor(Math.random() * messages.length)]);
                }
                
                // Add a little animation for fun
                e.target.style.transform = 'scale(1.2)';
                e.target.style.transition = 'transform 0.3s';
                setTimeout(() => {
                  e.target.style.transform = 'scale(1)';
                }, 300);
              }}
            >
              {userStats.totalImages}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Toplam Görsel
            </Typography>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="h3" sx={{ 
              mb: 1, 
              fontWeight: 700,
              color: theme.palette.primary.main
            }}>
              {userStats.totalProperties}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Toplam Mülk
            </Typography>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="h3" sx={{ 
              mb: 1, 
              fontWeight: 700,
              color: theme.palette.primary.main
            }}>
              {userStats.creditsRemaining}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Kalan Kredi
            </Typography>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="h3" sx={{ 
              mb: 1, 
              fontWeight: 700,
              color: theme.palette.primary.main
            }}>
              {userStats.achievementPoints}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Başarı Puanı
            </Typography>
          </StatCard>
        </Grid>
      </Grid>
      
      <StyledTabs
        value={tabValue}
        onChange={handleTabChange}
        centered
        variant="fullWidth"
      >
        <Tab label="Genel Bakış" />
        <Tab label="Başarılar" />
        <Tab label="Örnek Projeler" />
      </StyledTabs>
      
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <StyledPaper>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Son Oluşturulan Görseller
              </Typography>
              
              {recentImages.length > 0 ? (
                <Grid container spacing={2}>
                  {recentImages.map((image) => (
                    <Grid item xs={12} sm={6} key={image.id}>
                      <Card sx={{ 
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: theme.palette.mode === 'dark' 
                          ? '0 4px 20px rgba(0, 0, 0, 0.2)' 
                          : '0 4px 20px rgba(0, 0, 0, 0.05)',
                      }}>
                        <CardMedia
                          component="img"
                          height="160"
                          image={image.url}
                          alt={image.prompt?.substring(0, 30) || 'Generated image'}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="subtitle2" noWrap>
                            {image.prompt?.substring(0, 30) || 'Generated image'}...
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(image.created_at).toLocaleDateString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(0, 0, 0, 0.2)' 
                    : 'rgba(0, 0, 0, 0.03)',
                  borderRadius: 2
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Henüz hiç görsel oluşturmadınız.
                  </Typography>
                  <StyledButton
                    variant="contained"
                    onClick={() => navigate('/workflows')}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: '#FFFFFF',
                    }}
                  >
                    İlk Görselinizi Oluşturun
                  </StyledButton>
                </Box>
              )}
              
              {recentImages.length > 0 && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/gallery')}
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      textTransform: 'none',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: `${theme.palette.primary.main}10`,
                      }
                    }}
                  >
                    Tüm Görsellerinizi Görüntüleyin
                  </Button>
                </Box>
              )}
            </StyledPaper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <StyledPaper>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Hızlı Başlangıç
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate('/workflows')}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: '#FFFFFF',
                    textTransform: 'none',
                    borderRadius: 2,
                    padding: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    }
                  }}
                >
                  Yeni Görsel Oluştur
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/properties')}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    borderRadius: 2,
                    padding: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: `${theme.palette.primary.main}10`,
                    }
                  }}
                >
                  Mülklerinizi Yönetin
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/shared-links')}
                  sx={{
                    borderColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'rgba(0, 0, 0, 0.2)',
                    color: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.7)' 
                      : 'rgba(0, 0, 0, 0.7)',
                    textTransform: 'none',
                    borderRadius: 2,
                    padding: '12px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.3)' 
                        : 'rgba(0, 0, 0, 0.3)',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.05)',
                    }
                  }}
                >
                  Paylaşımlarınızı Görüntüleyin
                </Button>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Hesap Bilgileri
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  E-posta
                </Typography>
                <Typography variant="body1">
                  {userProfile?.email}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Üyelik Planı
                </Typography>
                <Typography variant="body1">
                  {userProfile?.subscription_tier || 'Ücretsiz Plan'}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Üyelik Tarihi
                </Typography>
                <Typography variant="body1">
                  {userProfile?.created_at 
                    ? new Date(userProfile.created_at).toLocaleDateString() 
                    : '-'}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={async () => {
                    try {
                      // Reset onboarding status in database
                      const { data: { user } } = await supabase.auth.getUser();
                      if (user) {
                        await supabase
                          .from('profiles')
                          .update({ onboarding_completed: false })
                          .eq('id', user.id);
                          
                        // Reload the page to trigger onboarding
                        window.location.reload();
                      }
                    } catch (error) {
                      console.error('Error resetting onboarding:', error);
                    }
                  }}
                  sx={{ 
                    textTransform: 'none',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main
                  }}
                >
                  Kurulumu Yeniden Başlat
                </Button>
              </Box>
            </StyledPaper>
          </Grid>
        </Grid>
      )}
      
      {tabValue === 1 && (
        <StyledPaper>
          <AchievementGallery />
        </StyledPaper>
      )}
      
      {tabValue === 2 && (
        <StyledPaper>
          <SampleProjectsGallery onSelectProject={handleSelectSampleProject} />
        </StyledPaper>
      )}
    </Box>
  );
};

export default UserDashboard;
